import { CFT } from '@repo/runtime-kit/sentry/sentry-tags';
import { useSentry } from '@repo/runtime-kit/sentry/context';
import { useCurrentDevice } from '@repo/shared-kit/hooks/use-current-device';
import { useTrackRender } from '@repo/shared-kit/hooks/use-track-render';
import { KycProvider } from '@repo/api-kit/queries/due-diligence/kyc';
import { IdnowProvider } from '#idnow-provider/idnow-provider';
import { FourthlineProvider } from '#fourthline-provider/fourthline-provider';
import { getProviderType } from '#utils/get-provider-type';
import { useSuspenseKycInfo } from '#hooks/use-kyc';
import { useRefetchProviderDetails } from '#hooks/use-refetch-provider-details';
import { useKycContext } from '../context/kyc-context';
import { useKycProviderDetails } from '../context/provider-details-context';
import { KycError } from '#error/error';

const providerComponents: Partial<Record<keyof typeof KycProvider, React.ReactNode>> = {
  [KycProvider.idnow]: <IdnowProvider />,
  [KycProvider.fourthline]: <FourthlineProvider />,
};

/**
 * Main KYC page component that renders the appropriate provider based on the KYC response.
 * Determines which verification provider (IDnow or Fourthline) to display.
 *
 * @returns The provider component or null if provider is unspecified
 */
export function KycPage() {
  const {
    membershipId,
    userId,
    trackingPrefix,
    legalCountry,
    isRegisterFlow,
    trackingEventOrigin,
  } = useKycContext();

  const { provider } = useKycProviderDetails();

  const market = isRegisterFlow ? legalCountry : undefined;
  const { data: kycInfo, refetch: refetchAcceptance } = useSuspenseKycInfo(market);
  const refetch = useRefetchProviderDetails(refetchAcceptance);
  const { isMobileBrowser } = useCurrentDevice();

  const providerType = getProviderType(kycInfo, provider);

  const sentry = useSentry();
  const hasInvalidProviderType = !providerComponents[providerType];

  useTrackRender({
    eventName: `${trackingPrefix}_kyc_started`,
    params: {
      membership_id: membershipId,
      provider: providerType,
      is_webview: isMobileBrowser,
      user_id: userId,
      is_new_kyc: true,
      type: kycInfo.activeProcess?.type,
      // FIXME: Should be deprecated in upcoming feature
      flow: kycInfo.activeProcess?.type,
      origin: trackingEventOrigin,
    },
  });

  if (hasInvalidProviderType) {
    sentry.captureException(new Error(`Unsupported KYC provider: ${providerType}`), {
      extra: {
        apiProvider: kycInfo.activeProcess?.provider,
        contextProvider: provider,
        provider: providerType,
        membershipId,
      },
      tags: {
        component: 'KycPage',
      },
      cft: CFT.IDENTITY_VERIFICATION,
    });

    return <KycError onRetry={refetch} />;
  }

  return providerComponents[providerType];
}
